/* eslint-disable camelcase */
/* eslint-disable max-lines */
// TODO: file is too long, split
import {
    MOBILE_REGEX,
    TRACKING_AVC_STEP,
    TRACKING_BUSINESS_TRANSACTION_TYPE,
    TRACKING_FLOW_TYPE,
    TRACKING_FORM_NAME,
    TRACKING_FORM_TYPE,
    TRACKING_PAGE_LEVEL_AVC,
    TRACKING_PAGE_TYPE,
    TRACKING_PLATFORM_TYPE_MOBILE,
    TRACKING_PRODUCT_LINE_P2,
    TRACKING_SITE_AREA,
    TRACKING_TRANSFER_POINT_AVAILABLE,
} from '../constants';
import {
    TrackingAVCPageView,
    TrackingGeneralPageView,
    TrackingOrder,
    TrackingProposition,
    TrackingProduct,
    TrackingAVCPageViewPayload,
} from '../interfaces/tracking';
import {
    TRACKING_BUNDLE,
    TRACKING_BTX_BNT,
    TRACKING_TARGET_AUDIENCE_CONSUMER,
    TRACKING_DISCOUNT,
    TRACKING_PLATFORM_TYPE_RESPONSIVE,
    TRACKING_NOT_LOGGED_IN,
    TRACKING_PAGE_TYPE_PRODUCT_DETAIL,
    TRACKING_SERVICE,
} from '@vfde-sails/tracking';
import { getCMSConfig } from './cms';
import { CableProviderType } from '../enums/product';
import { AccessSupplierType, TrackingAvcStep, TrackingProductType } from '../enums/tracking';
import { formatNumberToDecimalString } from './formatter';
import { AvcResult, JarvisProduct, ProductOption, TivanoData } from '@vfde-sails/avc-core';

let fiberSiteStructure: string[];

const getPlatformType = () => MOBILE_REGEX.test(navigator.userAgent)
    ? TRACKING_PLATFORM_TYPE_MOBILE
    : TRACKING_PLATFORM_TYPE_RESPONSIVE;

const getAProduct = (tariff: JarvisProduct, proposition?: number): TrackingProduct => {
    const { once, monthly } = tariff.price;
    const product: TrackingProduct = {
        sku: tariff.id,
        name: tariff.id,
        // Need to replace fibre to fiber,
        // because tracking expects fiber but jarvis gives back fibre -.-
        type: tariff.productType.replace('fibre', 'fiber'),
        duration: +tariff.duration || 24,
        ...proposition && {
            proposition: 1,
            units: 1,
        },
    };

    if ('number' === typeof once) {
        product.price_once = formatNumberToDecimalString(once);
    } else if ('number' === typeof monthly) {
        product.price_monthly = formatNumberToDecimalString(monthly);
    }

    return product;
};

const getAProposition = (tariff: JarvisProduct): TrackingProposition => ({
    id: 1,
    name: tariff.name,
    target_audience: TRACKING_TARGET_AUDIENCE_CONSUMER,
    retention: TRACKING_BTX_BNT,
    // Need to replace fibre to fiber,
    // because tracking expects fiber but jarvis gives back fibre -.-
    product_type: tariff.productType.replace('fibre', 'fiber'),
    product_line: TRACKING_PRODUCT_LINE_P2,
    product_category: TRACKING_BUNDLE,
    units: 1,
});

const getOptionString = (value: number, isDiscount = false) => isDiscount
    ? formatNumberToDecimalString(`-${Math.abs(value)}`)
    : formatNumberToDecimalString(`${Math.abs(value)}`);

const getOptionTrackingProduct = (option: ProductOption, duration: string, isDiscount = false) => {
    const { once, monthly } = option.price;
    const optionItem: TrackingProduct = {
        sku: option.id,
        name: option.id,
        type: isDiscount ? TRACKING_DISCOUNT : TRACKING_SERVICE,
        proposition: 1,
        units: 1,
    };

    if ('number' === typeof once) {
        // Price "Einmalig"
        optionItem.price_once = getOptionString(once, isDiscount);
    } else if ('number' === typeof monthly) {
        if (option.discount) {
            // Stairway price
            optionItem.duration = option.discount.month;
            optionItem.price_monthly = getOptionString(option.discount.gross, isDiscount);
        } else {
            // Monthly price
            optionItem.duration = +duration;
            optionItem.price_monthly = getOptionString(monthly, isDiscount);
        }
    }

    return optionItem;
};

/**
 * Iterate through all the discounts in a tariff, and if there is match
 * with the specified metacode, then returns it (in 'aProduct' format).
 * @param tariff The tariff in which we are looking for the discount
 * @param metacode Search discount with this metacode in the tariff
 * @returns The matching discount in aProduct format for tracking
 */
export const getOptionByMetacode = (
    tariff?: JarvisProduct,
    metacode?: string | RegExp,
    isDiscount = false,
): TrackingProduct | undefined => {
    if (!tariff?.discount || 0 >= tariff.discount.gross || !metacode) { return; }

    const optionArray = [
        ...tariff.options ?? [], // Standard discounts
        ...tariff.specialOffers ?? [], // Voucher codes
    ];
    const option = optionArray.find(item => 'string' === typeof metacode
        ? item.id === metacode
        : metacode.test(item.id)
    );

    if (!option) { return; }

    return getOptionTrackingProduct(option, tariff.duration, isDiscount);
};

export const getDeviceByMetacode = (
    tariff?: JarvisProduct,
    metacode?: string | RegExp,
    isDiscount = false,
): TrackingProduct | undefined => {
    if (!tariff?.discount || 0 >= tariff.discount.gross || !metacode || !tariff.devices) { return; }

    const device = tariff.devices.find(item => 'string' === typeof metacode
        ? item.id === metacode
        : metacode.test(item.id)
    );

    if (!device) { return; }

    return getOptionTrackingProduct(device, tariff.duration, isDiscount);

};

export const getGeneralPageView = (): TrackingGeneralPageView => {
    if (!fiberSiteStructure) {
        fiberSiteStructure = (window as any)?.VF?.siteStructure;
    }

    return {
        login_status: TRACKING_NOT_LOGGED_IN,
        page_levels: fiberSiteStructure,
        page_type: TRACKING_PAGE_TYPE_PRODUCT_DETAIL,
        site_area: TRACKING_SITE_AREA,
        business_transaction_type: TRACKING_BUSINESS_TRANSACTION_TYPE,
        line_of_customer: TRACKING_TARGET_AUDIENCE_CONSUMER,
        flow_type: TRACKING_FLOW_TYPE,
        platform_type: getPlatformType(),
    };
};

export const getTrackingOrder = (
    selectedTariff: JarvisProduct,
    isGigakombi: boolean,
    isFnf: boolean,
): TrackingOrder => {
    const {
        stairway = '',
        gigakombi = '',
        fnf = '',
        provisionFee = '',
        installationFee = '',
        provisionFeeDiscount = '',
        routerDiscount = '',
    } = getCMSConfig()?.visibleDiscountMetacodes || {};

    return {
        aProducts: [
            getAProduct(selectedTariff, 1),
            getOptionByMetacode(selectedTariff, RegExp(provisionFee, 'g')),
            getOptionByMetacode(selectedTariff, provisionFeeDiscount, true),
            getOptionByMetacode(selectedTariff, installationFee),
            getOptionByMetacode(selectedTariff, RegExp(stairway, 'g'), true),
            getDeviceByMetacode(selectedTariff, routerDiscount, true),
            isGigakombi && getOptionByMetacode(selectedTariff, gigakombi, true),
            isFnf && getOptionByMetacode(selectedTariff, RegExp(fnf, 'g'), true),
        ].filter((aProduct): aProduct is TrackingProduct => Boolean(aProduct)),
        aPropositions: [getAProposition(selectedTariff)],
    };
};

export const getAvailabilityFiberProviders = (
    passedAccessSupplier: string | undefined,
    passedAvailabilityIndicator: string | undefined,
    tivano: TivanoData | undefined,
) => {
    const isBHS = tivano?.metacodes.includes(getCMSConfig()?.homesPassedIds?.markerBHS ?? '');
    const isBKV = tivano?.metacodes.includes(getCMSConfig()?.homesPassedIds?.markerBKV ?? '');
    if (isBHS) { return ['vodafone-homepassed-bhs']; };
    if (isBKV && !isBHS) { return ['vodafone-homepassed-bkv']; };
    if ((!passedAccessSupplier || passedAccessSupplier === AccessSupplierType.VFDE)
        && !isBHS && !isBKV && !passedAvailabilityIndicator) {
        return ['vodafone-presales'];
    }

    const availabilityMap: Record<string, Record<string, string[]>> = {
        [AccessSupplierType.VFDE]: {
            'GRUEN': ['vodafone-connected'],
            'GRUEN_GRAU': ['vodafone-connected'],
        },
        [AccessSupplierType.OXG]: {
            'GRUEN': ['oxg-connected'],
            'GRUEN_GRAU': ['oxg-connected'],
            'GELB_GRAU': ['oxg-presales'],
            'ORANGE': ['oxg-homepassed'],
            'ORANGE_ROT': ['oxg-homepassed'],
        },
        [AccessSupplierType.DTAG]: {
            'GRUEN': ['telekom-connected'],
            'GRUEN_GRAU': ['telekom-connected'],
            'GELB_GRAU': ['telekom-presales'],
            'ORANGE': ['telekom-homepassed'],
            'ORANGE_ROT': ['telekom-homepassed'],
        },
        [AccessSupplierType.DGW]: {
            'GRUEN': ['deutsche glasfaser-connected'],
            'GRUEN_GRAU': ['deutsche glasfaser-connected'],
            'GELB_GRAU': ['deutsche glasfaser-presales'],
            'ORANGE': ['deutsche glasfaser-homepassed'],
            'ORANGE_ROT': ['deutsche glasfaser-homepassed'],
        },
    };

    if (passedAccessSupplier && passedAvailabilityIndicator) {
        const provider = availabilityMap[passedAccessSupplier]?.[passedAvailabilityIndicator];
        if (provider) {
            return provider;
        }
    }
};

export const getTrackingOfferProps = (result?: AvcResult, accessSupplier?: any,
    availabilityIndicator?: any, tivano?: TivanoData) => {
    const isBHS = tivano?.metacodes.includes(getCMSConfig()?.homesPassedIds?.markerBHS ?? '');
    const isBKV = tivano?.metacodes.includes(getCMSConfig()?.homesPassedIds?.markerBKV ?? '');
    if (!result) { return; }

    const { selected, fastest, availability, providerType, connectionType, selfInstall } = result;

    if (!selected || !fastest || !availability) { return; }
    const payload: TrackingAVCPageViewPayload = {
        selected_product: getAProduct(selected),
        selected_product_available: result.selectedAvailable,
        recommended_products: [fastest.id],
        available_technologies: [
            ...availability.fiber ? [TrackingProductType.FIBER] : [],
            ...availability.cable ? [TrackingProductType[providerType]] : [],
            ...availability.dsl ? [TrackingProductType.DSL] : [],
        ],
        available_bandwidth: `${fastest.download} mbit`,
        tv_connection_type: connectionType.toLowerCase(),
        self_installation_available: selfInstall,
        product_footprint: CableProviderType[providerType],
        transfer_point_available: TRACKING_TRANSFER_POINT_AVAILABLE,
    };
    if (result.availability.fiber || isBHS || isBKV) {
        payload.availability_fiber_providers =
            getAvailabilityFiberProviders(accessSupplier, availabilityIndicator, tivano);
    };
    return payload;
};

export const getAVCPageView = (
    selectedTariff: JarvisProduct,
    avcResult: AvcResult | undefined,
    step: TrackingAvcStep,
    accessSupplier?: string,
    availabilityIndicator?: string,
    tivano?: TivanoData,
): Partial<TrackingAVCPageView> => {
    if (!fiberSiteStructure) {
        fiberSiteStructure = (window as any)?.VF?.siteStructure;
    }

    const stepData = TRACKING_AVC_STEP[step];

    return {
        page_levels: fiberSiteStructure?.concat([TRACKING_PAGE_LEVEL_AVC, stepData.form_step_name]),
        page_type: TRACKING_PAGE_TYPE,
        form_type: TRACKING_FORM_TYPE,
        form_name: TRACKING_FORM_NAME,
        selected_product: getAProduct(selectedTariff),
        ...stepData,
        ...step === TrackingAvcStep.COMPLETE && getTrackingOfferProps(avcResult,
            accessSupplier, availabilityIndicator, tivano),
    };
};
